import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { BillingSubscription } from '@/types/billing'
import { AppDispatch, RootState } from '@/redux'
import { useDispatch, useSelector } from 'react-redux'
import { createPortalSession, updatePlaybookCount } from '@/services/billingService'
import { showNotification } from '@/services/notificationService'
import { TCSAPIResponse } from '@/types/types'
import { formatCurrency } from '@/helpers/billing'
import { Minus, Plus, ExternalLink } from 'lucide-react'
import { MAX_PLAYBOOKS, MIN_PLAYBOOKS } from '@/constants/TCS/billing'

/** Same as CurrentPlan: first $150/mo, each additional $75/mo. */
function getPlaybookMonthlyTotalCents(playbookCount: number): number {
  if (playbookCount < 1) return 0
  return 15000 + (playbookCount - 1) * 7500
}

interface PlaybookCountControlProps {
  token: string
  organizationId: string
  subscription: BillingSubscription
  onUpdated: () => void
}

export const PlaybookCountControl: React.FC<PlaybookCountControlProps> = ({
  token,
  organizationId,
  subscription,
  onUpdated
}) => {
  const dispatch: AppDispatch = useDispatch()
  const { loading: appLoading } = useSelector((state: RootState) => state.app)
  const targetCount = subscription.scheduledQuota ?? subscription.confirmedQuota ?? 1
  const usedSlots = subscription.usedSlots ?? 0
  const minCount = Math.max(MIN_PLAYBOOKS, usedSlots)
  const [value, setValue] = useState(targetCount)
  const [loading, setLoading] = useState(false)
  const hasPendingIncrease = subscription.hasPendingIncrease === true
  const pendingQuota = subscription.pendingQuota ?? 0
  const effectiveForPending = subscription.usableQuota ?? subscription.confirmedQuota ?? targetCount
  const additionalPlaybooks = Math.max(0, pendingQuota - effectiveForPending)
  const pendingChargeCents =
    effectiveForPending >= 1
      ? additionalPlaybooks * 7500
      : getPlaybookMonthlyTotalCents(pendingQuota)
  const pendingChargeLabel =
    effectiveForPending >= 1
      ? additionalPlaybooks === 1
        ? '1 additional playbook'
        : `${additionalPlaybooks} additional playbooks`
      : `${pendingQuota} playbook${pendingQuota !== 1 ? 's' : ''}`

  useEffect(() => {
    setValue(targetCount)
  }, [targetCount])

  const handleOpenPortal = async () => {
    const resp = await dispatch(createPortalSession(token, organizationId))
    if (resp.data?.url) {
      window.location.href = resp.data.url
    } else {
      showNotification({ data: undefined, err: resp.err }, '', {})
    }
  }

  const handleUpdate = async () => {
    if (value === targetCount || value < minCount || value > MAX_PLAYBOOKS) return
    setLoading(true)
    const resp = await dispatch(updatePlaybookCount(token, organizationId, value))
    setLoading(false)
    if (resp.data) {
      onUpdated()
      showNotification(
        { data: resp.data as unknown as TCSAPIResponse, err: undefined },
        'Playbook count updated',
        {}
      )
    } else {
      showNotification({ data: undefined, err: resp.err ?? 'Update failed' }, '', {})
    }
  }

  const canDecrease = value > minCount
  const canIncrease = value < MAX_PLAYBOOKS
  const hasChange = value !== targetCount
  const hasPendingDecrease = subscription.hasPendingDecrease === true
  const effective = subscription.usableQuota ?? subscription.confirmedQuota ?? targetCount
  const periodEndFormatted = new Date(subscription.currentPeriodEnd * 1000).toLocaleDateString()
  const newMonthlyRateCents = targetCount >= 1 ? getPlaybookMonthlyTotalCents(targetCount) : 0

  return (
    <div className="px-6 py-5">
      <h2 className="mb-4 text-base font-semibold text-gray-900">Adjust playbooks</h2>
      {hasPendingIncrease && (
        <div className="mb-4 rounded-lg border border-yellow-200 bg-yellow-50 p-3">
          <p className="text-sm font-medium text-yellow-800">
            Payment pending: <strong>{formatCurrency(pendingChargeCents / 100)}/mo</strong> for {pendingChargeLabel}.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={handleOpenPortal}
            disabled={appLoading}
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            Open Customer Portal
          </Button>
        </div>
      )}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
        <div className="flex-1 space-y-2">
          <Label htmlFor="playbook-count" className="text-gray-700">
            Playbooks
          </Label>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              disabled={!canDecrease || loading}
              onClick={() => setValue((v) => Math.max(minCount, v - 1))}
              aria-label="Decrease playbook count"
            >
              <Minus className="h-4 w-4" />
            </Button>
            <input
              id="playbook-count"
              type="number"
              min={minCount}
              max={MAX_PLAYBOOKS}
              value={value}
              onChange={(e) => setValue(Math.min(MAX_PLAYBOOKS, Math.max(minCount, Number(e.target.value) || minCount)))}
              className="h-10 w-20 rounded-md border border-gray-300 bg-white px-3 text-center text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              disabled={loading}
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              disabled={!canIncrease || loading}
              onClick={() => setValue((v) => Math.min(MAX_PLAYBOOKS, v + 1))}
              aria-label="Increase playbook count"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <Button
          onClick={handleUpdate}
          disabled={!hasChange || loading || hasPendingIncrease}
          className="sm:self-end"
        >
          {loading ? 'Updating…' : 'Update'}
        </Button>
      </div>
      {hasPendingDecrease && effective > targetCount && (
        <div className="mt-3 space-y-1 text-sm text-gray-600">
          <p>
            You have {effective} playbook{effective !== 1 ? 's' : ''} until {periodEndFormatted}. After that: {targetCount}.
          </p>
          <p>
            Your new monthly charge (from {periodEndFormatted}) will be {formatCurrency(newMonthlyRateCents / 100)} for {targetCount} playbook{targetCount !== 1 ? 's' : ''}.
          </p>
        </div>
      )}
    </div>
  )
}
